import { InlineNotification } from '@carbon/react';
import { useEffect, useMemo, useState } from 'react';

import request from 'lib/request';
import { SlotRollupNRITable } from 'pages/crm-tracts-subtracts/nri/slot-rollup-nri-table';
import { mapSlotNRIResponseToSlotNRI } from 'utils/misc';
import type { SlotNRIResponse } from 'types/api-responses';
import type { TractNRI } from 'components/nri-table/types';

interface EntitySlotsViewProps {
  entityId: number;
}

/**
 * Renders the entity's slot NRI table on the IC workspace page, showing
 * how certified interests are reflected across the system (certified vs
 * system promoted). Fetches from the entity slots endpoint which applies
 * the certified interest overlay.
 *
 * @param {EntitySlotsViewProps} props - Component props.
 * @return {JSX.Element} The entity slots table.
 */
export const EntitySlotsView = ({
  entityId,
}: EntitySlotsViewProps): JSX.Element => {
  const [data, setData] = useState<SlotNRIResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const controller = new AbortController();
    request
      .get<SlotNRIResponse>(`/entities/${entityId}/slots`, controller)
      .then((response) => {
        if (cancelled) return;
        if ('error' in response) {
          if ((response as { error: string }).error !== 'AbortError') {
            setError('Failed to load entity slots');
          }
        } else {
          setData(response);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [entityId]);

  const slots: TractNRI[] = useMemo(() => {
    if (!data) return [];
    return mapSlotNRIResponseToSlotNRI(data);
  }, [data]);

  return (
    <div style={{ padding: '1rem 0' }}>
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title={error}
          hideCloseButton={false}
        />
      )}
      <SlotRollupNRITable
        slotNRIRows={slots}
        isLoading={isLoading}
        tableId="ic-workspace-entity-slots"
      />
    </div>
  );
};
